import React, { useState } from 'react';
import { useTranslation, Trans } from '../../i18n';
import { roundScore } from '../../utils';
import cognitiveAbilitiesIllustration from '@public/images/cognitive_abilities_module.png';
import globalStyles from '../../styles/global.module.css';
import SingleBarChart from '../../components/SingleBarChart';
import SectionWithHeader from '../../components/SectionWithHeader';
import QuestionList from '../../components/QuestionList';
import { InfoTableData } from '../../typings';
import InfoTable from '../../components/InfoTable';
import QuestionsToAskModal from '../../components/QuestionsToAskModal';
import BenefitsAndChallenges from '../../components/BenefitsAndChallenges';

interface CognitiveAbilitiesStressResilienceProps {
    stressResilienceScore?: number;
    userRole: 'recruiter' | 'candidate';
    isWeb: boolean;
}

const CognitiveAbilitiesStressResilience = (
    props: CognitiveAbilitiesStressResilienceProps,
) => {
    const { stressResilienceScore, isWeb, userRole } = props;
    const { t } = useTranslation();
    const cognitiveStressResilienceScore = roundScore(stressResilienceScore);
    const [showQuestionsModal, setShowQuestionsModal] =
        useState<boolean>(false);
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

    const cognitiveStressResilienceExplanation = () => {
        let scoreLabel: string;
        if (
            cognitiveStressResilienceScore >= 0 &&
            cognitiveStressResilienceScore < 40
        ) {
            scoreLabel = 'GOOD';
        } else if (
            cognitiveStressResilienceScore >= 40 &&
            cognitiveStressResilienceScore < 60
        ) {
            scoreLabel = 'GREAT';
        } else if (
            cognitiveStressResilienceScore >= 60 &&
            cognitiveStressResilienceScore <= 100
        ) {
            scoreLabel = 'EXCELLENT';
        } else {
            scoreLabel = 'NO_SCORE';
        }

        if (scoreLabel === 'NO_SCORE') {
            return (
                <p className={`${globalStyles.text}`}>
                    <Trans
                        i18nKey={`COGNITIVE_ABILITIES.STRESS_RESILIENCE.USER_SCORE.${scoreLabel}`}
                    />
                </p>
            );
        } else {
            return (
                <>
                    <p className={`${globalStyles.text}`}>
                        <Trans
                            i18nKey={`COGNITIVE_ABILITIES.STRESS_RESILIENCE.USER_SCORE.${scoreLabel}.PARAGRAPH_1`}
                            components={{
                                bold: <strong />,
                                italic: <i />,
                                color: <span style={{ color: '#00A5AC' }} />,
                            }}
                        />
                    </p>
                    <p
                        className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                        <Trans
                            i18nKey={`COGNITIVE_ABILITIES.STRESS_RESILIENCE.USER_SCORE.${scoreLabel}.PARAGRAPH_2`}
                        />
                    </p>
                    <p
                        className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                        <Trans
                            i18nKey={`COGNITIVE_ABILITIES.STRESS_RESILIENCE.USER_SCORE.${scoreLabel}.PARAGRAPH_3`}
                        />
                    </p>
                </>
            );
        }
    };

    const cognitiveAbilitiesStressResilienceImprove = () => {
        const tableData: InfoTableData = {
            type: 'INFORMATION',
            leftColumn: {
                data: [],
            },
            rightColumn: {
                data: [],
            },
        };

        for (let dataCount = 1; dataCount < 10; dataCount++) {
            if (
                stressResilienceScore &&
                stressResilienceScore >= 0 &&
                stressResilienceScore < 40 &&
                dataCount < 6
            ) {
                if (dataCount < 4) {
                    tableData.leftColumn.data.push({
                        description: t(
                            `COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.GOOD.DESC_${dataCount}`,
                        ),
                    });
                }
                if (dataCount > 3 && dataCount < 6) {
                    tableData.rightColumn.data.push({
                        description: t(
                            `COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.GOOD.DESC_${dataCount}`,
                        ),
                    });
                }
            }
            if (
                stressResilienceScore &&
                stressResilienceScore >= 40 &&
                stressResilienceScore < 60 &&
                dataCount < 6
            ) {
                if (dataCount < 4) {
                    tableData.leftColumn.data.push({
                        description: t(
                            `COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.GREAT.DESC_${dataCount}`,
                        ),
                    });
                }
                if (dataCount > 3 && dataCount < 6) {
                    tableData.rightColumn.data.push({
                        description: t(
                            `COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.GREAT.DESC_${dataCount}`,
                        ),
                    });
                }
            }
            if (
                stressResilienceScore &&
                stressResilienceScore >= 60 &&
                stressResilienceScore <= 100
            ) {
                if (dataCount < 6) {
                    tableData.leftColumn.data.push({
                        description: t(
                            `COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.EXCELLENT.DESC_${dataCount}`,
                        ),
                    });
                }
                if (dataCount > 5 && dataCount < 10) {
                    tableData.rightColumn.data.push({
                        description: t(
                            `COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.EXCELLENT.DESC_${dataCount}`,
                        ),
                    });
                }
            }
        }

        return (
            <InfoTable
                cardTitle={
                    stressResilienceScore &&
                    stressResilienceScore >= 0 &&
                    stressResilienceScore < 60
                        ? t(
                              'COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.TITLE',
                          )
                        : t(
                              'COGNITIVE_ABILITIES.STRESS_RESILIENCE.IMPROVE.EXCELLENT.TITLE',
                          )
                }
                isWeb={isWeb}
                tableData={tableData}
            />
        );
    };

    const cognitiveAbilitiesStressResilienceQuestions = () => {
        const tableData = [];
        for (let dataCount = 1; dataCount < 6; dataCount++) {
            if (
                stressResilienceScore &&
                stressResilienceScore >= 0 &&
                stressResilienceScore < 40
            ) {
                tableData.push(
                    t(
                        `COGNITIVE_ABILITIES.STRESS_RESILIENCE.QUESTIONS.GOOD.DESC_${dataCount}`,
                    ),
                );
            }
            if (
                stressResilienceScore &&
                stressResilienceScore >= 40 &&
                stressResilienceScore < 60
            ) {
                tableData.push(
                    t(
                        `COGNITIVE_ABILITIES.STRESS_RESILIENCE.QUESTIONS.GREAT.DESC_${dataCount}`,
                    ),
                );
            }
            if (
                stressResilienceScore &&
                stressResilienceScore >= 60 &&
                stressResilienceScore <= 100
            ) {
                tableData.push(
                    t(
                        `COGNITIVE_ABILITIES.STRESS_RESILIENCE.QUESTIONS.EXCELLENT.DESC_${dataCount}`,
                    ),
                );
            }
        }
        return tableData;
    };

    return (
        <>
            <SectionWithHeader
                title={t('COGNITIVE_ABILITIES.TITLE')}
                subTitle={t('COGNITIVE_ABILITIES.STRESS_RESILIENCE.TITLE')}
                headerImage={cognitiveAbilitiesIllustration}
                hasHeader={!isWeb}
                handleInfoDetails={() => setShowInfoModal(true)}
                isWeb={isWeb}>
                <>
                    {!isWeb && (
                        <>
                            <p className={`${globalStyles.text}`}>
                                <Trans
                                    i18nKey="COGNITIVE_ABILITIES.STRESS_RESILIENCE.EXPLANATION"
                                    components={{ bold: <strong /> }}
                                />
                            </p>
                            <QuestionList
                                title={t(
                                    'COGNITIVE_ABILITIES.STRESS_RESILIENCE.EXPLANATION_LIST',
                                )}
                                items={[
                                    t(
                                        'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_1',
                                    ),
                                    t(
                                        'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_2',
                                    ),
                                    t(
                                        'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_3',
                                    ),
                                    t(
                                        'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_4',
                                    ),
                                    t(
                                        'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_5',
                                    ),
                                    t(
                                        'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_6',
                                    ),
                                ]}
                            />
                        </>
                    )}
                </>
            </SectionWithHeader>
            <>
                {!!stressResilienceScore ? (
                    <SingleBarChart
                        value={cognitiveStressResilienceScore}
                        max={100}
                        hasScoreValue={userRole === 'recruiter'}
                        hasScoreLabel={true}
                        hasUserIcon={false}
                        hasScoreCheckpoints={true}
                        minLabel={t(
                            'COGNITIVE_ABILITIES.STRESS_RESILIENCE.BAR_CHART.MIN_LABEL',
                        )}
                        midLabel={t(
                            'COGNITIVE_ABILITIES.STRESS_RESILIENCE.BAR_CHART.MID_LABEL',
                        )}
                        maxLabel={t(
                            'COGNITIVE_ABILITIES.STRESS_RESILIENCE.BAR_CHART.MAX_LABEL',
                        )}
                        resultLabels={{
                            low: t('GENERAL_SINGLE_BAR_CHART.RESULT_LABEL.LOW'),
                            mid: t('GENERAL_SINGLE_BAR_CHART.RESULT_LABEL.MID'),
                            high: t(
                                'GENERAL_SINGLE_BAR_CHART.RESULT_LABEL.HIGH',
                            ),
                        }}
                    />
                ) : (
                    <div className={`${globalStyles.textNoScoreDisplay}`}>
                        <svg
                            width="24"
                            height="25"
                            viewBox="0 0 27 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <circle
                                cx="13.5"
                                cy="14.376"
                                r="13.5"
                                fill="#F2D04B"
                            />
                            <path
                                d="M12.32 7.87598H15.28L14.78 17.236H12.8L12.32 7.87598ZM13.8 22.016C13.3333 22.016 12.9467 21.8693 12.64 21.576C12.3333 21.2693 12.18 20.896 12.18 20.456C12.18 20.016 12.3333 19.6493 12.64 19.356C12.9467 19.0626 13.3333 18.916 13.8 18.916C14.2533 18.916 14.6267 19.0626 14.92 19.356C15.2267 19.6493 15.38 20.016 15.38 20.456C15.38 20.896 15.2267 21.2693 14.92 21.576C14.6267 21.8693 14.2533 22.016 13.8 22.016Z"
                                fill="white"
                            />
                        </svg>
                        <span
                            className={`${globalStyles.text} ${globalStyles.textPaddingTop} ${globalStyles.textNoBelowSpace} ${globalStyles.textNoAboveSpace}`}>
                            {t(`NO_SCORE_AVAILABLE`)}
                        </span>
                    </div>
                )}
                {cognitiveStressResilienceExplanation()}
                <br></br>
                {!!stressResilienceScore && stressResilienceScore !== 0 && (
                    <BenefitsAndChallenges
                        excellentLeftCount={5}
                        excellentRightCount={3}
                        goodLeftCount={3}
                        goodRightCount={4}
                        greatLeftCount={4}
                        greatRightCount={4}
                        isWeb={isWeb}
                        maxCount={6}
                        moduleType={'COGNITIVE_ABILITIES.STRESS_RESILIENCE'}
                        score={stressResilienceScore}
                    />
                )}
                <br></br>
                {!!stressResilienceScore &&
                    stressResilienceScore !== 0 &&
                    cognitiveAbilitiesStressResilienceImprove()}
                <br></br>
                {!isWeb &&
                    !!stressResilienceScore &&
                    stressResilienceScore !== 0 && (
                        <QuestionList
                            title={t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.QUESTIONS.TITLE_PDF',
                            )}
                            items={cognitiveAbilitiesStressResilienceQuestions()}
                        />
                    )}
                {isWeb &&
                    !!stressResilienceScore &&
                    stressResilienceScore !== 0 && (
                        <p
                            className={`${globalStyles.text}`}
                            style={{ cursor: 'pointer', color: '#00A5AC' }}
                            onClick={() => setShowQuestionsModal(true)}>
                            <Trans
                                i18nKey="COGNITIVE_ABILITIES.STRESS_RESILIENCE.QUESTIONS.TITLE_WEB"
                                components={{ bold: <strong /> }}
                            />
                        </p>
                    )}
                {showQuestionsModal &&
                    isWeb &&
                    !!stressResilienceScore &&
                    stressResilienceScore !== 0 && (
                        <QuestionsToAskModal
                            title={t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.QUESTIONS.TITLE_WEB',
                            )}
                            items={cognitiveAbilitiesStressResilienceQuestions()}
                            closeScanDetails={() =>
                                setShowQuestionsModal(false)
                            }
                        />
                    )}
                {showInfoModal && isWeb && (
                    <QuestionsToAskModal
                        title={t('GENERAL_SCORE_MEANING')}
                        descriptions={[
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.EXPLANATION_1',
                            ),
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.EXPLANATION_LIST',
                            ),
                        ]}
                        items={[
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_1',
                            ),
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_2',
                            ),
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_3',
                            ),
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_4',
                            ),
                            t(
                                'COGNITIVE_ABILITIES.STRESS_RESILIENCE.LIST_ITEM_5',
                            ),
                        ]}
                        closeScanDetails={() => setShowInfoModal(false)}
                    />
                )}
            </>
        </>
    );
};

export default CognitiveAbilitiesStressResilience;
